import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { StudentService } from '@core/services/student.service';
import { ActivatedRoute } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { PermissionService } from '@core/services/permission.service';
import { Permission } from '@core/enums/permissions';
import { TableColumnType } from '@core/enums/table';
import { IModalEmitData } from '@core/interfaces/imodal';
import { Modal } from '@core/enums/modal';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { StudentClassEnrollmentService } from '@core/services/student-class-enrollment.service';
import { take } from 'rxjs';
import { DropChildComponent } from '@views/user/manage-enrollment/drop-child/drop-child.component';
import { EditClassEnrollmentDate } from './edit-class-enrollment/edit-class-enrollment-date.component';
import { TranslocoService } from '@jsverse/transloco';
@Component({
    selector: 'drdp-view-class-enrollments',
    templateUrl: './view-class-enrollments.component.html',
    styleUrls: ['./view-class-enrollments.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ViewClassEnrollmentsComponent implements OnInit {
  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  tableData?: any;
  canDelete = this.permissionService.checkPermission(
    Permission.DeleteEnrollments
  );
  canEdit = this.permissionService.checkPermission(
    Permission.EditStudents
  );
  classId: number = 0;
  constructor(
    private studentService: StudentService,
    private studentClassEnrollmentService: StudentClassEnrollmentService,
    private permissionService: PermissionService,
    public modal: MatDialog,
    private route: ActivatedRoute,
    private translocoService: TranslocoService
  ) {}

  tableColumns: any[] = [];

  buildTableColumns(): void {
    this.tableColumns = [
      { columnDef: 'agencyName', header: this.translocoService.translate('user.manageEnrollment.viewChild.labels.agencyName'), type: 'text' },
      {
        columnDef: 'enrollmentStartDate',
        header: this.translocoService.translate('user.manageEnrollment.classEnrollments.table.agencyEnrollment'),
        type: 'date',
      },
      {
        columnDef: 'enrollmentEndDate',
        header: this.translocoService.translate('user.manageEnrollment.classEnrollments.table.agencyWithdrawal'),
        type: 'date',
      },
      {
        columnDef: 'ageGroup',
        header: this.translocoService.translate('user.manageEnrollment.classEnrollments.table.ageGradeInstrument'),
        type: 'text',
      },
      {
        columnDef: 'enrollmentType',
        header: this.translocoService.translate('user.manageEnrollment.classEnrollments.table.enrollment'),
        type: 'text',
      },
      { columnDef: 'ratingPeriodName', header: this.translocoService.translate('user.manageEnrollment.viewChild.labels.ratingPeriod'), type: 'text' },
      { columnDef: 'name', header: this.translocoService.translate('user.manageEnrollment.viewChild.labels.classroom'), type: 'text' },
      {
        columnDef: 'classEnrollmentDate',
        header: this.translocoService.translate('user.manageEnrollment.viewChild.labels.classroomStartDate'),
        type: 'date',
      },
      {
        columnDef: 'classExitDate',
        header: this.translocoService.translate('user.manageEnrollment.classEnrollments.table.classroomWithdrawDate'),
        type: 'date',
      },
      { columnDef: 'siteName', header: this.translocoService.translate('user.manageEnrollment.classEnrollments.table.site'), type: 'text' },
      this.canEdit ?
      {
        columnDef: 'edit',
        header: this.translocoService.translate('user.manageEnrollment.viewChild.actions.edit'),
        type: 'edit'
      }: null,
    ].filter(Boolean);
  }

  ngOnInit(): void {
    this.buildTableColumns();
    this.classId = Number(this.route.snapshot.paramMap.get('id'));
    if (this.canDelete) {
      this.tableColumns.push({
        columnDef: 'delete',
        header: this.translocoService.translate('user.manageEnrollment.viewChild.actions.delete'),
        type: TableColumnType.delete,
      });
    }
    this.getEnrollmentDetails();
  }

  getEnrollmentDetails() {
    this.studentService
      .getClassroomEnrollmentDetails(this.classId)
      .subscribe((res: any) => {
        this.dataSource = new MatTableDataSource(res);
        this.tableData = res;
      });
  }

  deleteModal(event: IModalEmitData): void {
    const isActiveEnrollment = event.data?.classExitDate == undefined;
    const isLastActive =
      isActiveEnrollment &&
      this.tableData.filter(
        (item: any) =>
          item.classExitDate == undefined && item.id != event.data?.id
      ).length == 0;
    let confirmationKey: string;
    if (isLastActive) {
      confirmationKey = 'user.manageEnrollment.classEnrollments.deleteDialog.confirmActiveLastEnrollment';
    } else if (isActiveEnrollment) {
      confirmationKey = 'user.manageEnrollment.classEnrollments.deleteDialog.confirmActive';
    } else {
      confirmationKey = 'user.manageEnrollment.classEnrollments.deleteDialog.confirm';
    }
    const confirmationMessage = this.translocoService.translate(confirmationKey);

    let modal: IModalEmitData = { data: {}, index: 0 };
    modal.data.modalInfo = {
      title: this.translocoService.translate('user.manageEnrollment.classEnrollments.deleteDialog.title'),
      message: confirmationMessage,
      primaryBtnText: Modal.OK,
      showCancel: true,
    };

    const modalRef = this.modal.open(ConfirmationModalComponent, {
      data: modal,
    });

    modalRef.afterClosed().subscribe((res) => {
      if (res) {
        if (isActiveEnrollment) {
          this.openUnenrollModal(event);
        } else {
          this.deleteEnrollment(event.data?.id ?? 0);
        }
      }
    });
  }

  openModel(editData: IModalEmitData) {
    const modalRef = this.modal.open(EditClassEnrollmentDate, {
      data: editData ? editData : undefined,
    });

    modalRef.afterClosed().subscribe((response) => {
      if (response.success) {
        this.getEnrollmentDetails();
      }
    });
  }

  deleteEnrollment(id: number) {
    this.studentClassEnrollmentService
      .deleteStudentClassEnrollment(id, this.translocoService.translate('user.manageEnrollment.classEnrollments.toast.enrollmentDeleted'))
      .pipe(take(1))
      .subscribe((success) => {
        if (success) {
          this.getEnrollmentDetails();
        }
      });
  }

  openUnenrollModal(data: IModalEmitData) {
    const enrollment = data.data;
    if (enrollment) {
      const modalData = {
        data: {
          id: enrollment.studentId,
          classId: enrollment.classId,
          siteId: enrollment.siteId,
          firstName: enrollment.firstName,
          lastName: enrollment.lastName,
          studentClassEnrollmentId: enrollment.id,
        },
      };
      const modalRef = this.modal.open(DropChildComponent, {
        data: modalData,
      });
      modalRef.afterClosed().subscribe((success) => {
        if (success) {
          this.deleteEnrollment(data.data?.id ?? 0);
        }
      });
    }
  }
}
